import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Breadcrumbs,
  Link,
  CircularProgress,
} from '@mui/material';
import {
  Security,
  Home,
  Article,
  SmartToy,
  Settings,
  Psychology,
  Webhook,
  Receipt,
} from '@mui/icons-material';
import { useNavigate, Navigate } from 'react-router-dom';
import { useCurrentUser } from '../../hooks/useApi';

export const SystemAdminPage: React.FC = () => {
  const { data: user, isLoading } = useCurrentUser();
  const navigate = useNavigate();

  // Show loading spinner while user data is being fetched
  if (isLoading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Redirect if user doesn't have permissions.all permission (after loading completes)
  if (!user?.permissions?.includes('permissions.all')) {
    return <Navigate to="/" replace />;
  }

  const adminPages = [
    {
      title: 'Permissions Management',
      description: 'Manage users, roles, and permissions for the portal',
      icon: <Security fontSize="large" />,
      action: () => navigate('/admin/permissions'),
      color: '#1976d2',
    },
    {
      title: 'Log Management',
      description: 'View and analyze system logs and LLM activity',
      icon: <Article fontSize="large" />,
      action: () => navigate('/admin/log-management'),
      color: '#2e7d32',
    },
    {
      title: 'Mia Oversight',
      description: 'Monitor and manage Mia chat interactions and performance',
      icon: <SmartToy fontSize="large" />,
      action: () => navigate('/admin/mia-oversight'),
      color: '#9c27b0',
    },
    {
      title: 'LLM Providers & Models',
      description: 'Configure LLM providers and models for AI-powered features',
      icon: <Settings fontSize="large" />,
      action: () => navigate('/admin/llm-management'),
      color: '#f57c00',
    },
    {
      title: 'LLM Prompts',
      description: 'Edit and configure AI prompts for report processing',
      icon: <Psychology fontSize="large" />,
      action: () => navigate('/admin/prompts'),
      color: '#9c27b0',
    },
    {
      title: 'HubSpot Webhook Logs',
      description: 'View and analyze HubSpot webhook events received by the system',
      icon: <Webhook fontSize="large" />,
      action: () => navigate('/admin/hubspot-webhook-logs'),
      color: '#ff7a59',
    },
    {
      title: 'QuickBooks Webhook Logs',
      description: 'View and analyze QuickBooks webhook events received by the system',
      icon: <Receipt fontSize="large" />,
      action: () => navigate('/admin/quickbooks-webhook-logs'),
      color: '#2CA01C',
    },
  ];

  // Helper function to render a card
  const renderCard = (page: any) => (
    <Grid item xs={12} md={6} lg={4} key={page.title}>
      <Card
        sx={{
          height: '100%',
          cursor: 'pointer',
          transition: 'all 0.3s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
          },
        }}
        onClick={page.action}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 60,
                height: 60,
                borderRadius: 2,
                bgcolor: page.color,
                color: 'white',
                mr: 2,
              }}
            >
              {page.icon}
            </Box>
            <Typography variant="h6" component="h3">
              {page.title}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {page.description}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <Security sx={{ mr: 0.5 }} fontSize="inherit" />
            System Administration
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            System Administration
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage system settings, permissions, and monitoring tools
          </Typography>
        </Box>

        {/* Admin Pages Grid */}
        <Grid container spacing={3}>
          {adminPages.map(renderCard)}
        </Grid>
      </Box>
    </Container>
  );
};
